import type { PropData } from '@/services/events/main-event';
import { countryCode } from '@/lib/country-flags';
import { getNflTeam } from '@/config/nfl-teams';

// what a −110 line prices at (apps/nextjs services/props/props.types.ts
// EVEN_MONEY_MULTIPLIER); the multiplier itself arrives with each prop, this
// only names the book primary among a ladder's alternates
const EVEN_MONEY_MULTIPLIER = 1.25;


/** Format goal totals to max 3 decimal places, trimming trailing zeros */
export function fmtPts(n: number): string {
  return parseFloat(n.toFixed(3)).toString();
}


/** Infer O/U side from player prop category or description */
export function getPlayerPropSide(p: PropData): 'OVER' | 'UNDER' | null {
  if (p.category?.endsWith('_OVER')) return 'OVER';
  if (p.category?.endsWith('_UNDER')) return 'UNDER';
  const desc = p.description.toLowerCase();
  if (desc.includes(' over ')) return 'OVER';
  if (desc.includes(' under ')) return 'UNDER';
  if (desc.includes(' o/u ')) return null; // Legacy "O/U" — unsplit
  return null;
}


/** Get base category without _OVER/_UNDER suffix */
export function getBaseCategory(category: string): string {
  return category.replace(/_OVER$/, '').replace(/_UNDER$/, '');
}


/**
 * True when a matchup is NFL — the event's sport says so, or (fallback for
 * callers without event context) the team name resolves to an NFL team.
 */
export function isNflMatch(sport?: string | null, teamName?: string | null): boolean {
  return sport === 'NFL' || !!getNflTeam(teamName ?? null);
}


/** Sport-aware label for the game-total section ("Total Goals" is soccer-speak). */
export function getTotalLabel(sport?: string | null, teamName?: string | null): string {
  return isNflMatch(sport, teamName) ? 'Total Points' : 'Total Goals';
}


/** Sport-aware unit for a player's points line preview. */
export function getPointsUnit(sport?: string | null): string {
  return sport === 'NFL' ? 'pts' : 'goals';
}


// ============================================================================
// PROP COMPONENTS
// ============================================================================

export const CATEGORY_LABELS: Record<string, string> = {
  SPREAD: 'Spread',
  TOTAL: 'Game Total',
  TOTAL_OVER: 'Total Over',
  TOTAL_UNDER: 'Total Under',
  TEAM_TOTAL: 'Team Total',
  TEAM_TOTAL_OVER: 'Team Total Over',
  TEAM_TOTAL_UNDER: 'Team Total Under',
  MONEYLINE: 'Moneyline',
  PLAYER_POINTS: 'Points',
  PLAYER_REBOUNDS: 'Rebounds',
  PLAYER_ASSISTS: 'Assists',
  PLAYER_POINTS_REBOUNDS: 'Pts+Reb',
  PLAYER_POINTS_ASSISTS: 'Pts+Ast',
  PLAYER_REBOUNDS_ASSISTS: 'Reb+Ast',
  // NFL stat categories — ids/labels mirror the nextjs app's
  // config/sports/nfl.ts propCategories so NFL props stop falling through to
  // the raw enum string.
  PASSING_YARDS: 'Passing Yards',
  PASSING_TDS: 'Passing TDs',
  PASS_COMPLETIONS: 'Completions',
  PASS_INTERCEPTIONS: 'Interceptions Thrown',
  RUSHING_YARDS: 'Rushing Yards',
  RUSH_ATTEMPTS: 'Rush Attempts',
  RECEIVING_YARDS: 'Receiving Yards',
  RECEPTIONS: 'Receptions',
  RUSH_RECEIVING_YARDS: 'Rush + Rec Yards',
  ANYTIME_TD: 'Anytime TD',
  KICKING_POINTS: 'Kicking Points',
  SACKS: 'Sacks',
  // The categories the odds import actually writes for player props carry a
  // PLAYER_ prefix (PLAYER_MARKETS_BY_SPORT in prop-description.ts) — the
  // unprefixed ids above are the sport-config namespace, and the two are
  // deliberately separate. Without these the picks UI shows the raw enum.
  PLAYER_PASS_YARDS: 'Passing Yards',
  PLAYER_PASS_TDS: 'Passing TDs',
  PLAYER_PASS_COMPLETIONS: 'Completions',
  PLAYER_PASS_INTERCEPTIONS: 'Interceptions Thrown',
  PLAYER_RUSH_YARDS: 'Rushing Yards',
  PLAYER_RUSH_ATTEMPTS: 'Rush Attempts',
  PLAYER_RECEIVING_YARDS: 'Receiving Yards',
  PLAYER_RECEPTIONS: 'Receptions',
  PLAYER_RUSH_RECEIVING_YARDS: 'Rush + Rec Yards',
  PLAYER_ANYTIME_TD: 'Anytime TD',
  PLAYER_KICKING_POINTS: 'Kicking Points',
  PLAYER_SACKS: 'Sacks',
};


/** Format a line value with +/- sign */
export function formatLine(line: number | null): string {
  if (line === null) return '—';
  return line > 0 ? `+${line}` : `${line}`;
}


/** Derive a short abbreviation from a team name (e.g. "Michigan St Spartans" → "MSU") */
export const TEAM_ABBREV_MAP: Record<string, string> = {
  // Big Ten
  'michigan state': 'MSU', 'michigan st': 'MSU', 'michigan st spartans': 'MSU', 'michigan state spartans': 'MSU',
  'michigan': 'MICH', 'michigan wolverines': 'MICH',
  'ohio state': 'OSU', 'ohio st': 'OSU', 'ohio state buckeyes': 'OSU', 'ohio st buckeyes': 'OSU',
  'penn state': 'PSU', 'penn st': 'PSU', 'penn state nittany lions': 'PSU',
  'iowa': 'IOWA', 'iowa hawkeyes': 'IOWA',
  'iowa state': 'ISU', 'iowa st': 'ISU', 'iowa state cyclones': 'ISU', 'iowa st cyclones': 'ISU',
  'illinois': 'ILL', 'illinois fighting illini': 'ILL',
  'purdue': 'PUR', 'purdue boilermakers': 'PUR',
  'indiana': 'IND', 'indiana hoosiers': 'IND',
  'wisconsin': 'WIS', 'wisconsin badgers': 'WIS',
  'minnesota': 'MINN', 'minnesota golden gophers': 'MINN',
  'northwestern': 'NW', 'northwestern wildcats': 'NW',
  'nebraska': 'NEB', 'nebraska cornhuskers': 'NEB',
  'maryland': 'MD', 'maryland terrapins': 'MD',
  'rutgers': 'RUT', 'rutgers scarlet knights': 'RUT',
  // ACC
  'duke': 'DUKE', 'duke blue devils': 'DUKE',
  'north carolina': 'UNC', 'north carolina tar heels': 'UNC',
  'nc state': 'NCST', 'north carolina state': 'NCST', 'nc state wolfpack': 'NCST',
  'virginia': 'UVA', 'virginia cavaliers': 'UVA',
  'virginia tech': 'VT', 'virginia tech hokies': 'VT',
  'clemson': 'CLEM', 'clemson tigers': 'CLEM',
  'louisville': 'LOU', 'louisville cardinals': 'LOU',
  'pittsburgh': 'PITT', 'pittsburgh panthers': 'PITT',
  'wake forest': 'WAKE', 'wake forest demon deacons': 'WAKE',
  'syracuse': 'SYR', 'syracuse orange': 'SYR',
  'florida state': 'FSU', 'florida st': 'FSU', 'florida state seminoles': 'FSU',
  'georgia tech': 'GT', 'georgia tech yellow jackets': 'GT',
  'notre dame': 'ND', 'notre dame fighting irish': 'ND',
  // SEC
  'alabama': 'BAMA', 'alabama crimson tide': 'BAMA',
  'auburn': 'AUB', 'auburn tigers': 'AUB',
  'tennessee': 'TENN', 'tennessee volunteers': 'TENN',
  'kentucky': 'UK', 'kentucky wildcats': 'UK',
  'florida': 'FLA', 'florida gators': 'FLA',
  'georgia': 'UGA', 'georgia bulldogs': 'UGA',
  'arkansas': 'ARK', 'arkansas razorbacks': 'ARK',
  'mississippi state': 'MSST', 'mississippi st': 'MSST', 'mississippi state bulldogs': 'MSST',
  'ole miss': 'MISS', 'ole miss rebels': 'MISS', 'mississippi': 'MISS',
  'lsu': 'LSU', 'lsu tigers': 'LSU',
  'south carolina': 'SC', 'south carolina gamecocks': 'SC',
  'texas a&m': 'TAMU', 'texas am': 'TAMU', 'texas a&m aggies': 'TAMU',
  'missouri': 'MIZ', 'missouri tigers': 'MIZ',
  'vanderbilt': 'VAN', 'vanderbilt commodores': 'VAN',
  // Big 12
  'kansas': 'KU', 'kansas jayhawks': 'KU',
  'kansas state': 'KSU', 'kansas st': 'KSU', 'kansas state wildcats': 'KSU',
  'baylor': 'BAY', 'baylor bears': 'BAY',
  'texas tech': 'TTU', 'texas tech red raiders': 'TTU',
  'oklahoma': 'OU', 'oklahoma sooners': 'OU',
  'oklahoma state': 'OKST', 'oklahoma st': 'OKST', 'oklahoma state cowboys': 'OKST',
  'west virginia': 'WVU', 'west virginia mountaineers': 'WVU',
  'tcu': 'TCU', 'tcu horned frogs': 'TCU',
  'texas': 'TEX', 'texas longhorns': 'TEX',
  'cincinnati': 'CIN', 'cincinnati bearcats': 'CIN',
  'houston': 'HOU', 'houston cougars': 'HOU',
  'byu': 'BYU', 'byu cougars': 'BYU',
  'ucf': 'UCF', 'ucf knights': 'UCF',
  'colorado': 'COL', 'colorado buffaloes': 'COL',
  'arizona': 'ARIZ', 'arizona wildcats': 'ARIZ',
  'arizona state': 'ASU', 'arizona st': 'ASU', 'arizona state sun devils': 'ASU',
  'utah': 'UTAH', 'utah utes': 'UTAH',
  // Pac-12 remnants / WCC / other
  'gonzaga': 'GONZ', 'gonzaga bulldogs': 'GONZ',
  'uconn': 'UCONN', 'uconn huskies': 'UCONN', 'connecticut': 'UCONN', 'connecticut huskies': 'UCONN',
  'creighton': 'CREI', 'creighton bluejays': 'CREI',
  'marquette': 'MARQ', 'marquette golden eagles': 'MARQ',
  'villanova': 'NOVA', 'villanova wildcats': 'NOVA',
  'xavier': 'XAV', 'xavier musketeers': 'XAV',
  'seton hall': 'SH', 'seton hall pirates': 'SH',
  'st johns': 'STJ', "st john's": 'STJ', "st. john's": 'STJ',
  'san diego state': 'SDSU', 'san diego st': 'SDSU',
  'memphis': 'MEM', 'memphis tigers': 'MEM',
  'usc': 'USC', 'usc trojans': 'USC',
  'ucla': 'UCLA', 'ucla bruins': 'UCLA',
  'oregon': 'ORE', 'oregon ducks': 'ORE',
  'washington': 'WASH', 'washington huskies': 'WASH',
};


export function getAbbrev(teamName: string): string {
  // Strip seed prefix like "(3) "
  const clean = teamName.replace(/^\(\d+\)\s*/, '').trim();
  const key = clean.toLowerCase();

  // NFL teams are seeded with city display names ("Kansas City",
  // "New York (G)") — resolve to the standard abbreviation (KC, NYG).
  const nfl = getNflTeam(clean);
  if (nfl) return nfl.abbr;

  // Countries (e.g. World Cup teams) use the standard FIFA 3-letter code
  // ("Czechia" → "CZE", "South Korea" → "KOR", "South Africa" → "RSA").
  const country = countryCode(clean);
  if (country) return country;

  // Exact match
  if (TEAM_ABBREV_MAP[key]) return TEAM_ABBREV_MAP[key];

  // Try without trailing mascot (e.g., "Michigan State Spartans" → "michigan state")
  const words = clean.split(' ');
  for (let i = words.length - 1; i >= 1; i--) {
    const partial = words.slice(0, i).join(' ').toLowerCase();
    if (TEAM_ABBREV_MAP[partial]) return TEAM_ABBREV_MAP[partial];
  }

  // Fallback: first word, max 4 chars uppercase
  return (words.length === 1 ? words[0] : words[0].slice(0, 4)).toUpperCase();
}


/** Extract player name from prop description like "Lionel Messi — Goals O/U 1.5" */
export function extractPlayerName(description: string): string {
  const dashIdx = description.indexOf(' — ');
  if (dashIdx > 0) return description.substring(0, dashIdx);
  return 'Unknown';
}

// ============================================================================
// SIMPLE OVER/UNDER — the plain question a line is really asking
// ============================================================================

/** Infer O/U side from a game-total prop's category, or its wording if legacy. */
export function getTotalPropSide(p: PropData): 'OVER' | 'UNDER' | null {
  if (p.category === 'TOTAL_OVER') return 'OVER';
  if (p.category === 'TOTAL_UNDER') return 'UNDER';
  // Legacy unsplit TOTAL category — the side only exists in the wording.
  const desc = p.description.toLowerCase();
  if (desc.includes('over')) return 'OVER';
  if (desc.includes('under')) return 'UNDER';
  return null;
}

/**
 * The smallest whole number that clears a line.
 *
 * Settlement is strictly greater-than and an exact landing pushes (see the
 * TOTAL_OVER / player branches of settleUndercardProps), and every stat we
 * settle against — points, yards, receptions — is a whole number. So "Over
 * 44.5" and "45 or more" are the same bet, and that equivalence is what lets a
 * card ask a plain question instead of showing the user a decimal. Whole lines
 * step up the same way: "Over 45" is only won at 46, because 45 itself pushes.
 */
export function clearingNumber(line: number): number {
  return Math.floor(line) + 1;
}

/** True when a line can land exactly — which refunds both sides as a push. */
export function canPush(line: number): boolean {
  return Number.isInteger(line);
}

/**
 * Lineless markets ("Anytime TD") read as a plain statement rather than an
 * over/under, so their question keeps the label and drops the threshold.
 */
export function isLinelessProp(p: PropData): boolean {
  return !/\b(over|under)\b/i.test(p.description);
}

export interface MainLine {
  /** The book's line both sides are priced at. */
  line: number;
  /** The whole number the question asks about — "N or more". */
  threshold: number;
  /** Bought when the user answers Yes (the Over). */
  yes: PropData | null;
  /** Bought when the user answers No (the Under). Null on one-sided markets. */
  no: PropData | null;
}

/**
 * The primary line of a market, as the yes/no pair it really is.
 *
 * Every line is priced from its own odds (services/props/props.types.ts),
 * so no multiplier marks the primary by itself. Two things do. The book
 * prices BOTH sides only at its primary — alternates arrive as a lone side,
 * which is why the admin import's Under ladder is one line long — and both
 * of those sides sit near even money, where an alternate has one short side
 * and one long. So a line carrying two sides beats any line carrying one, and
 * among lines carrying the same number, a line with an Over outranks one
 * without — the Over names the line, and the card asks the Over's question —
 * and only then does the line whose worse-priced side is nearest even money
 * win. Anchoring on the single prop nearest even money —
 * the old rule — chose a QB's −231 alternate over his +168 primary, asked
 * "2+" about a 2.5 line, and found no Under there to pair with it.
 */
export function findMainLine(
  props: PropData[],
  sideOf: (p: PropData) => 'OVER' | 'UNDER' | null,
): MainLine | null {
  const withLine = props.filter((p) => p.line !== null);
  if (withLine.length === 0) return null;

  const byLine = groupBy(withLine, (p) => p.line as number);
  // A legacy prop whose side can't be read at all still counts as one side —
  // and still answers "yes" when bought, which is what the old single Buy
  // button meant.
  const line = primaryKey(byLine, (ps) => {
    const sides = new Set(ps.map(sideOf).filter(Boolean));
    return Math.max(1, sides.size) * 2 + (sides.has('OVER') ? 1 : 0);
  });
  const atLine = byLine.get(line)!;
  const sideless = withLine.every((p) => sideOf(p) === null);

  return {
    line,
    threshold: clearingNumber(line),
    yes: atLine.find((p) => sideOf(p) === 'OVER') ?? (sideless ? atLine[0] : null),
    no: atLine.find((p) => sideOf(p) === 'UNDER') ?? null,
  };
}

function groupBy<K>(props: PropData[], keyOf: (p: PropData) => K): Map<K, PropData[]> {
  const groups = new Map<K, PropData[]>();
  for (const p of props) {
    const key = keyOf(p);
    const group = groups.get(key);
    if (group) group.push(p);
    else groups.set(key, [p]);
  }
  return groups;
}

/**
 * The group the book priced as its primary: highest rank first (the caller's
 * count of sides, with the side that names the question breaking ties), then
 * the group whose worse-priced side is nearest even money. Equal groups keep
 * the earlier one.
 */
function primaryKey<K>(groups: Map<K, PropData[]>, rankOf: (props: PropData[]) => number): K {
  let best: K | undefined;
  let bestRank = -1;
  let bestDistance = Infinity;
  for (const [key, props] of groups) {
    const rank = rankOf(props);
    const distance = Math.max(...props.map((p) => Math.abs(p.bonusMultiplier - EVEN_MONEY_MULTIPLIER)));
    if (rank > bestRank || (rank === bestRank && distance < bestDistance)) {
      best = key;
      bestRank = rank;
      bestDistance = distance;
    }
  }
  return best as K;
}

/** Word for what a game total counts — NFL scores points, soccer scores goals. */
export function getTotalNoun(sport?: string | null, teamName?: string | null): string {
  return isNflMatch(sport, teamName) ? 'points' : 'goals';
}

/** e.g. "Will there be 45 or more points scored in this game?" */
export function buildTotalQuestion(threshold: number, noun: string): string {
  return `Will there be ${threshold} or more ${noun} scored in this game?`;
}

/** e.g. "25+ Receiving Yards?" — or just "Anytime TD?" for lineless markets. */
export function buildPlayerQuestion(threshold: number, label: string, lineless: boolean): string {
  return lineless ? `${label}?` : `${threshold}+ ${label}?`;
}

export interface MainSpread {
  /** The favourite's line, always negative (e.g. -3.5). */
  line: number;
  /** Winning margin that clears it — "win by N or more". */
  threshold: number;
  /** Favourite's name, exactly as the game names it. */
  favourite: string;
  /** Bought when the user answers Yes — the favourite covering. */
  yes: PropData | null;
  /** Bought when the user answers No — the underdog covering. */
  no: PropData | null;
}

/**
 * The primary spread, as the yes/no pair it really is.
 *
 * Same rule as findMainLine: the magnitude priced for both teams is the
 * primary, and even-money distance only separates magnitudes with the same
 * number of sides. The two teams' props at that magnitude ARE the two
 * answers — taking the underdog at +3.5 is precisely answering "no" to the
 * favourite winning by 4 or more, so the market needs one question and not
 * a team toggle.
 *
 * A pick'em (line 0) returns null: with no margin to clear, the question
 * collapses to "who wins", which the card already asks for free above.
 */
export function findMainSpread(
  props: PropData[],
  homeTeam: string,
  awayTeam: string,
): MainSpread | null {
  const withLine = props.filter((p) => p.line !== null && p.line !== 0);
  if (withLine.length === 0) return null;

  const byMagnitude = groupBy(withLine, (p) => Math.abs(p.line as number));
  // The favourite names the question, so a magnitude carrying the favourite
  // outranks one carrying only the underdog.
  const magnitude = primaryKey(byMagnitude, (ps) => {
    const signs = new Set(ps.map((p) => Math.sign(p.line as number)));
    return signs.size * 2 + (signs.has(-1) ? 1 : 0);
  });
  const atLine = byMagnitude.get(magnitude)!;

  const yes = atLine.find((p) => (p.line as number) < 0) ?? null;
  const no = atLine.find((p) => (p.line as number) > 0) ?? null;
  // The favourite names the question; without its side there is nothing to ask.
  if (!yes) return null;

  const favourite = [homeTeam, awayTeam].find((t) => yes.description.includes(t)) ?? homeTeam;

  return { line: yes.line as number, threshold: clearingNumber(magnitude), favourite, yes, no };
}

/** e.g. "Will Kansas City win by 4 or more?" */
export function buildSpreadQuestion(threshold: number, favourite: string): string {
  return `Will ${favourite} win by ${threshold} or more?`;
}

